import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-auth-error-banner.css?inline';
import {
    PK_AUTH_ERROR_MAP,
    lookupAuthEntry,
    type PkAuthErrorEntry,
    type PkAuthCtaSpec,
    type PkAuthTone,
} from './pk-auth-error-banner.constants.js';

/**
 * `<pk-auth-error-banner>` — canonical display surface for API auth-error
 * slugs. Takes a slug (e.g. `refresh-token-replayed`, `totp-invalid`), looks
 * up the user-facing headline + body + CTA, and renders it with the right
 * tone (error/warn/info) + ARIA semantics.
 *
 * See `docs/components/pk-auth-error-banner.md` for the full slug catalogue.
 *
 * Why this exists: the `message` field on `{ error, message, correlationId }`
 * API responses is developer-facing copy. Consumers were each reinventing
 * the slug → user-facing message mapping, drifting from each other and
 * occasionally leaking dev copy ("Reader role required for this tenant")
 * to end users. This component is the single source of truth.
 *
 * Attributes:
 *   - `slug` — required. The API error slug. Unknown slugs render the
 *              fallback entry with a `console.warn`.
 *   - `correlation-id` — optional. Renders in monospace at the bottom; click
 *                        to copy.
 *   - `retry-after-seconds` — required when `slug="rate-limited"`. Renders
 *                              the countdown into the body copy.
 *   - `tone` — optional override (`error` | `warn` | `info`). Default is
 *              inferred from the slug's mapping entry.
 *   - `compact` — boolean. Hides the body, renders headline + CTA only.
 *
 * Slots:
 *   - default — optional override for the body copy (slug mapping still
 *               drives headline + CTA + tone).
 *   - `extra` — optional content below the CTA row.
 *
 * Events:
 *   - `pk-auth-cta-click` — `CustomEvent<{ slug, action }>`. Fires when the
 *     primary or secondary CTA is activated. Consumer handles routing.
 *   - `pk-auth-correlation-copied` — `CustomEvent<{ correlationId }>`. Fires
 *     after the correlation-id is copied to the clipboard.
 */
export class PkAuthErrorBanner extends HTMLElement {
    static readonly tagName = 'pk-auth-error-banner';

    static get observedAttributes(): string[] {
        return ['slug', 'correlation-id', 'retry-after-seconds', 'tone', 'compact'];
    }

    private readonly _root: ShadowRoot;
    private _copiedFlashUntil = 0;
    private _lastWarnedSlug: string | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        // Reset any "copied!" flash if the user navigates between slugs.
        this._copiedFlashUntil = 0;
        this.render();
    }

    private _resolveEntry(): { entry: PkAuthErrorEntry; isFallback: boolean; slug: string } {
        const slug = (this.getAttribute('slug') ?? '').trim();
        const { entry, isFallback } = lookupAuthEntry(slug);
        if (isFallback && slug && this._lastWarnedSlug !== slug) {
            console.warn(`pk-auth-error-banner: unknown slug "${slug}". Using fallback.`);
            this._lastWarnedSlug = slug;
        }
        return { entry, isFallback, slug: slug || 'unknown' };
    }

    private _resolveTone(entry: PkAuthErrorEntry): PkAuthTone {
        const attr = this.getAttribute('tone');
        if (attr === 'error' || attr === 'warn' || attr === 'info') return attr;
        return entry.tone;
    }

    private _formatBody(entry: PkAuthErrorEntry): string {
        let body = entry.body;
        const correlationId = this.getAttribute('correlation-id') ?? '';
        body = body.replaceAll('{correlation-id}', correlationId || '—');

        const rawRetry = this.getAttribute('retry-after-seconds');
        if (rawRetry !== null) {
            const seconds = Number.parseInt(rawRetry, 10);
            const display = Number.isFinite(seconds) && seconds > 0 ? String(seconds) : rawRetry;
            body = body.replaceAll('{retry-after-seconds}', display);
        } else {
            body = body.replaceAll('{retry-after-seconds}', '—');
        }
        return body;
    }

    private readonly _onCtaClick = (cta: PkAuthCtaSpec) => (e: Event): void => {
        e.preventDefault();
        const { slug } = this._resolveEntry();
        this.dispatchEvent(new CustomEvent('pk-auth-cta-click', {
            detail:   { slug, action: cta.action },
            bubbles:  true,
            composed: true,
        }));
    };

    private readonly _onCorrelationClick = async (): Promise<void> => {
        const correlationId = this.getAttribute('correlation-id') ?? '';
        if (!correlationId) return;

        try {
            // navigator.clipboard may be unavailable in jsdom; degrade silently.
            const clip = (globalThis as { navigator?: Navigator }).navigator?.clipboard;
            if (clip && typeof clip.writeText === 'function') {
                await clip.writeText(correlationId);
            }
        } catch {
            // Swallow — analytics event still fires so consumer can capture the
            // intent. Components don't surface "copy failed" UX in v0.1.
        }

        this._copiedFlashUntil = Date.now() + 2000;
        this.dispatchEvent(new CustomEvent('pk-auth-correlation-copied', {
            detail:   { correlationId },
            bubbles:  true,
            composed: true,
        }));
        this.render();
        // Schedule a re-render to clear the "copied!" flash.
        setTimeout(() => {
            if (Date.now() >= this._copiedFlashUntil) {
                this.render();
            }
        }, 2050);
    };

    private _renderCta(cta: PkAuthCtaSpec | null, slot: 'primary' | 'secondary'): TemplateResult | typeof nothing {
        if (!cta) return nothing;
        if (slot === 'primary') {
            return html`
                <a
                    class="cta-primary"
                    href=${cta.href ?? '#'}
                    @click=${this._onCtaClick(cta)}
                    data-action=${cta.action}
                >${cta.label}</a>
            `;
        }
        return html`
            <button
                type="button"
                class="cta-secondary"
                @click=${this._onCtaClick(cta)}
                data-action=${cta.action}
            >${cta.label}</button>
        `;
    }

    private template(): TemplateResult {
        const { entry, slug }    = this._resolveEntry();
        const tone               = this._resolveTone(entry);
        const isCompact          = this.hasAttribute('compact');
        const role               = tone === 'info' ? 'status' : 'alert';
        const ariaLive           = tone === 'info' ? 'polite' : 'assertive';
        const correlationId      = this.getAttribute('correlation-id') ?? '';
        const showCopiedFlash    = Date.now() < this._copiedFlashUntil;
        const formattedBody      = this._formatBody(entry);
        const icon               = tone === 'info' ? 'ℹ' : tone === 'warn' ? '⚠' : '✕';

        return html`
            <style>${styles}</style>
            <div
                class="banner banner--${tone}${isCompact ? ' banner--compact' : ''}"
                role=${role}
                aria-live=${ariaLive}
                data-slug=${slug}
            >
                <p class="headline">
                    <span class="icon" aria-hidden="true">${icon}</span>
                    <span>${entry.headline}</span>
                </p>

                ${isCompact ? nothing : html`
                    <p class="body">
                        <slot>${formattedBody}</slot>
                    </p>
                `}

                ${entry.primaryCta || entry.secondaryCta ? html`
                    <div class="cta-row">
                        ${this._renderCta(entry.primaryCta, 'primary')}
                        ${this._renderCta(entry.secondaryCta, 'secondary')}
                    </div>
                ` : nothing}

                <div class="extra-row"><slot name="extra"></slot></div>

                ${correlationId ? html`
                    <div class="correlation">
                        <span>Reference:</span>
                        <button
                            type="button"
                            class="correlation-id"
                            title="Click to copy"
                            @click=${this._onCorrelationClick}
                        >${correlationId}</button>
                        ${showCopiedFlash ? html`<span class="correlation-copied">Copied</span>` : nothing}
                    </div>
                ` : nothing}
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkAuthErrorBanner.tagName)) {
    customElements.define(PkAuthErrorBanner.tagName, PkAuthErrorBanner);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-auth-error-banner': PkAuthErrorBanner;
    }
}

export { PK_AUTH_ERROR_MAP, lookupAuthEntry };
export type { PkAuthErrorEntry, PkAuthCtaSpec, PkAuthTone };
